import React, { useEffect, useMemo, useRef, useState } from "react";
import "./userprofile.css";
import { supabase } from "../../supabase_client.jsx";

const BANNER_PRESETS = [
  { label: "Midnight", value: "linear-gradient(120deg, #0b1220 0%, #6b2626 40%, #6d28d9 100%)" },
  { label: "Ocean",    value: "linear-gradient(120deg, #03045e 0%, #0077b6 50%, #90e0ef 100%)" },
  { label: "Forest",   value: "linear-gradient(120deg, #1b4332 0%, #2d6a4f 50%, #52b788 100%)" },
  { label: "Ember",    value: "linear-gradient(120deg, #370617 0%, #9d0208 50%, #f48c06 100%)" },
  { label: "Dusk",     value: "linear-gradient(120deg, #10002b 0%, #5a189a 50%, #e0aaff 100%)" },
  { label: "Slate",    value: "linear-gradient(120deg, #212529 0%, #495057 60%, #adb5bd 100%)" },
];

export default function UserProfile({ currentUser, onViewSelect }) {
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [stepOneAttempted, setStepOneAttempted] = useState(false);
  const [setupStep, setSetupStep] = useState(1);

  // photo upload states
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const avatarInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  // ── Event data ──
  const [savedEvents, setSavedEvents] = useState([]);
  const [goingEvents, setGoingEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  // ── Connections count ──
  const [connectionsCount, setConnectionsCount] = useState(null);

  // Setup wizard form
  const [setupForm, setSetupForm] = useState({
    fullName: "",
    age: "",
    gender: "",
    bio: "",
    preferences: [],
    avatar_url: "",
    banner_url: "",
    banner_color: BANNER_PRESETS[0].value,
    banner_type: "color", // "color" | "image"
  });

  const PREFERENCE_OPTIONS = [
    "Food", "Sports", "Music", "Movies", "Gaming",
    "Travel", "Fitness", "Art", "Tech", "Outdoors",
  ];

  // Base profile shown on page (username/email still from users table)
  const [profile, setProfile] = useState({
    fullName: "",
    username: "",
    email: "",
    bio: "",
  });

  const initials = useMemo(() => {
    if (!profile.fullName) return "??";
    const parts = profile.fullName.trim().split(" ").filter(Boolean);
    const a = parts[0]?.[0] || "?";
    const b = parts[1]?.[0] || parts[0]?.[1] || "?";
    return (a + b).toUpperCase();
  }, [profile.fullName]);

  // Ensure meta row exists
  async function ensureMetaRow(userId) {
    const { data, error } = await supabase
      .from("user_profile_meta")
      .select("*")
      .eq("user_id", userId)
      .single();

    if (!error && data) return data;

    await supabase.from("user_profile_meta").upsert({
      user_id: userId,
      has_setup_profile: false,
      preferences: [],
    });

    const { data: newRow } = await supabase
      .from("user_profile_meta")
      .select("*")
      .eq("user_id", userId)
      .single();

    return newRow;
  }

  // ── Load profile meta ──
  useEffect(() => {
    async function load() {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      if (!user) return;

      const { data: userRow } = await supabase
        .from("users")
        .select("*")
        .eq("user_id", user.id)
        .single();

      const metaRow = await ensureMetaRow(user.id);
      setMeta(metaRow);

      const nameFromMeta = (metaRow?.name || "").trim();

      setProfile({
        fullName: nameFromMeta,
        username: userRow?.username || "",
        email: userRow?.email || user.email || "",
        bio: userRow?.bio || "",
      });

      if (!metaRow?.has_setup_profile) {
        setShowSetupModal(true);
      }

      setSetupForm({
        fullName: nameFromMeta,
        age: metaRow?.age || "",
        gender: metaRow?.gender || "",
        bio: metaRow?.bio || "",
        preferences: metaRow?.preferences || [],
        avatar_url: metaRow?.avatar_url || "",
        banner_url: metaRow?.banner_url || "",
        banner_color: metaRow?.banner_color || BANNER_PRESETS[0].value,
        banner_type: metaRow?.banner_url ? "image" : "color",
      });

      setLoading(false);
    }

    load();
  }, []);

  // ── Load saved + going events ──
  useEffect(() => {
    const userId = currentUser?.user?.id || currentUser?.id;
    if (!userId) return;

    async function loadEvents() {
      setEventsLoading(true);
      try {
        // Fetch bookmark IDs, going IDs, and accepted connections in parallel
        const [bmRes, yesRes, connRes] = await Promise.all([
          supabase.from("bookmarked_events").select("event_id").eq("user_id", userId),
          supabase.from("marked_yes_events").select("event_id").eq("user_id", userId),
          supabase
            .from("connection_requests")
            .select("id", { count: "exact", head: true })
            .eq("status", "accepted")
            .or(`requester_id.eq.${userId},receiver_id.eq.${userId}`),
        ]);

        const bmIds = (bmRes.data || []).map((r) => Number(r.event_id)).filter(Number.isFinite);
        const yesIds = (yesRes.data || []).map((r) => Number(r.event_id)).filter(Number.isFinite);

        // Only saved (bookmarked but NOT going)
        const savedOnlyIds = bmIds.filter((id) => !yesIds.includes(id));

        // Fetch event rows — only if there are IDs to look up
        const [savedRows, goingRows] = await Promise.all([
          savedOnlyIds.length > 0
            ? supabase.from("events").select("id, name, date, image_url, location").in("id", savedOnlyIds)
            : Promise.resolve({ data: [] }),
          yesIds.length > 0
            ? supabase.from("events").select("id, name, date, image_url, location").in("id", yesIds)
            : Promise.resolve({ data: [] }),
        ]);

        setSavedEvents(savedRows.data || []);
        setGoingEvents(goingRows.data || []);
        setConnectionsCount(connRes.count ?? 0);
      } catch (e) {
        console.error("UserProfile: failed to load events", e);
      } finally {
        setEventsLoading(false);
      }
    }

    loadEvents();
  }, [currentUser]);

  function togglePreference(label) {
    setSetupForm((prev) => {
      const cur = Array.isArray(prev.preferences) ? prev.preferences : [];
      if (cur.includes(label)) {
        return { ...prev, preferences: cur.filter((p) => p !== label) };
      }
      return { ...prev, preferences: [...cur, label] };
    });
  }

  // upload avatar to supabase storage
  async function handleAvatarUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true);
    try {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData?.user?.id;
      const ext = file.name.split(".").pop();
      const path = `user-avatars/${userId}_${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("profile-images")
        .upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from("profile-images").getPublicUrl(path);
      setSetupForm(prev => ({ ...prev, avatar_url: urlData.publicUrl }));
    } catch (err) {
      alert("Avatar upload failed: " + err.message);
    } finally {
      setAvatarUploading(false);
    }
  }

  // upload banner to supabase storage
  async function handleBannerUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBannerUploading(true);
    try {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData?.user?.id;
      const ext = file.name.split(".").pop();
      const path = `user-banners/${userId}_${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("profile-images")
        .upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from("profile-images").getPublicUrl(path);
      setSetupForm(prev => ({ ...prev, banner_url: urlData.publicUrl, banner_type: "image" }));
    } catch (err) {
      alert("Banner upload failed: " + err.message);
    } finally {
      setBannerUploading(false);
    }
  }

  async function completeSetup() {
    const { data: authData } = await supabase.auth.getUser();
    const user = authData?.user;
    if (!user) return;

    const nameClean = (setupForm.fullName || "").trim();

    await supabase.from("user_profile_meta").upsert({
      user_id: user.id,
      has_setup_profile: true,
      name: nameClean,
      age: setupForm.age ? Number(setupForm.age) : null,
      gender: setupForm.gender || null,
      bio: setupForm.bio || null,
      preferences: setupForm.preferences,
      avatar_url: setupForm.avatar_url || null,
      banner_url: setupForm.banner_type === "image" ? setupForm.banner_url : null,
      banner_color: setupForm.banner_color,
      updated_at: new Date().toISOString(),
    });

    setMeta({
      ...meta,
      ...setupForm,
      name: nameClean,
      has_setup_profile: true,
    });

    setProfile((p) => ({ ...p, fullName: nameClean || p.fullName }));

    setShowSetupModal(false);
    setSetupStep(1);
  }

  const ageDisplay = meta?.age ?? "—";
  const genderDisplay = meta?.gender ?? "—";
  const bioDisplay = meta?.bio || profile.bio || "—";
  const prefsDisplay = Array.isArray(meta?.preferences) ? meta.preferences : [];

  const bannerStyle = meta?.banner_url
    ? { backgroundImage: `url(${meta.banner_url})`, backgroundSize: "cover", backgroundPosition: "center", background: "none" }
    : { background: meta?.banner_color || BANNER_PRESETS[0].value };

  if (loading) return <div className="profilePage">Loading...</div>;

  // ── Small helper: renders one event row in the profile cards ──
  function EventRow({ event }) {
    return (
      <div className="profileEventRow">
        {event.image_url ? (
          <img src={event.image_url} alt={event.name} className="profileEventThumb" />
        ) : (
          <div className="profileEventThumbPlaceholder">🎟</div>
        )}
        <div className="profileEventInfo">
          <div className="profileEventName">{event.name}</div>
          <div className="profileEventMeta">{event.date}{event.location ? ` · ${event.location}` : ""}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="profilePage">
      {/* Setup modal */}
      {showSetupModal && (
        <div className="modalOverlay">
          <div className="modalCard">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
              <h2 style={{ margin: 0 }}>Set up your profile</h2>
              <button
                onClick={() => setShowSetupModal(false)}
                style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#888", lineHeight: 1 }}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <p>Step {setupStep} of 3</p>

            {setupStep === 1 && (
              <>
                <div className="upAvatarSection">
                  <div className="upAvatarPreview">
                    {setupForm.avatar_url
                      ? <img src={setupForm.avatar_url} alt="avatar" />
                      : <span>{initials}</span>
                    }
                  </div>
                  <div className="upAvatarActions">
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleAvatarUpload}
                    />
                    <button
                      className="btnPrimary"
                      onClick={() => avatarInputRef.current?.click()}
                      disabled={avatarUploading}
                    >
                      {avatarUploading ? "Uploading…" : "Upload Photo"}
                    </button>
                    {setupForm.avatar_url && (
                      <button
                        className="btnGhost"
                        onClick={() => setSetupForm(p => ({ ...p, avatar_url: "" }))}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>

                <label>Name</label>
                <input
                  value={setupForm.fullName}
                  onChange={(e) => setSetupForm({ ...setupForm, fullName: e.target.value })}
                  placeholder="Your name"
                />

                <label>Age</label>
                <input
                  value={setupForm.age}
                  onChange={(e) => setSetupForm({ ...setupForm, age: e.target.value })}
                  placeholder="e.g. 21"
                />

                <label>Gender</label>
                <input
                  value={setupForm.gender}
                  onChange={(e) => setSetupForm({ ...setupForm, gender: e.target.value })}
                  placeholder="e.g. Female / Male / Prefer not to say"
                />

                <div className="modalActions" style={{ flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                  {stepOneAttempted && (!setupForm.fullName.trim() || !setupForm.age.toString().trim() || !setupForm.gender.trim()) && (
                    <p style={{ fontSize: 12, color: "crimson", margin: 0 }}>
                      Please fill out Name, Age and Gender to continue.
                    </p>
                  )}
                  <button
                    className="btnPrimary"
                    onClick={() => {
                      setStepOneAttempted(true);
                      if (setupForm.fullName.trim() && setupForm.age.toString().trim() && setupForm.gender.trim()) {
                        setSetupStep(2);
                      }
                    }}
                  >
                    Next →
                  </button>
                </div>
              </>
            )}

            {setupStep === 2 && (
              <>
                <p style={{ fontWeight: 700, marginBottom: 8 }}>Banner Style</p>
                <div className="bannerTypeTabs">
                  <button
                    className={"bannerTypeTab" + (setupForm.banner_type === "color" ? " active" : "")}
                    onClick={() => setSetupForm(p => ({ ...p, banner_type: "color" }))}
                  >
                    🎨 Color
                  </button>
                  <button
                    className={"bannerTypeTab" + (setupForm.banner_type === "image" ? " active" : "")}
                    onClick={() => setSetupForm(p => ({ ...p, banner_type: "image" }))}
                  >
                    🖼 Photo
                  </button>
                </div>

                <div
                  className="bannerPreview"
                  style={
                    setupForm.banner_type === "image" && setupForm.banner_url
                      ? { backgroundImage: `url(${setupForm.banner_url})`, backgroundSize: "cover", backgroundPosition: "center" }
                      : { background: setupForm.banner_color }
                  }
                />

                {setupForm.banner_type === "color" && (
                  <>
                    <p style={{ fontWeight: 700, marginTop: 12, marginBottom: 6 }}>Pick a gradient</p>
                    <div className="bannerPresets">
                      {BANNER_PRESETS.map(p => (
                        <button
                          key={p.label}
                          className={"bannerPreset" + (setupForm.banner_color === p.value ? " active" : "")}
                          style={{ background: p.value }}
                          title={p.label}
                          onClick={() => setSetupForm(prev => ({ ...prev, banner_color: p.value }))}
                        />
                      ))}
                    </div>
                  </>
                )}

                {setupForm.banner_type === "image" && (
                  <div style={{ marginTop: 12 }}>
                    <input
                      ref={bannerInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleBannerUpload}
                    />
                    <button
                      className="btnPrimary"
                      onClick={() => bannerInputRef.current?.click()}
                      disabled={bannerUploading}
                    >
                      {bannerUploading ? "Uploading…" : "Upload Banner Photo"}
                    </button>
                    {setupForm.banner_url && (
                      <button
                        className="btnGhost"
                        style={{ marginLeft: 8 }}
                        onClick={() => setSetupForm(p => ({ ...p, banner_url: "", banner_type: "color" }))}
                      >
                        Remove
                      </button>
                    )}
                    <p style={{ fontSize: 12, color: "#888", marginTop: 6 }}>
                      Wide landscape image works best (1500×400px).
                    </p>
                  </div>
                )}

                <div className="modalActions">
                  <button className="btnGhost" onClick={() => setSetupStep(1)}>← Back</button>
                  <button className="btnPrimary" onClick={() => setSetupStep(3)}>Next →</button>
                </div>
              </>
            )}

            {setupStep === 3 && (
              <>
                <label>Bio</label>
                <textarea
                  value={setupForm.bio}
                  onChange={(e) => setSetupForm({ ...setupForm, bio: e.target.value })}
                  placeholder="A short bio..."
                  rows={4}
                />

                <div style={{ marginTop: 10, fontWeight: 700 }}>Preferences</div>
                <div className="chips">
                  {PREFERENCE_OPTIONS.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      className={"chip" + (setupForm.preferences.includes(opt) ? " active" : "")}
                      onClick={() => togglePreference(opt)}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                <div className="modalActions">
                  <button className="btnGhost" onClick={() => setSetupStep(2)}>← Back</button>
                  <button className="btnPrimary" onClick={completeSetup}>Finish ✓</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="profileShell">
        {/* TOP NAV */}
        <div className="calendar-top-nav">
          <div className="nav-left">
            <button className="nav-tab" onClick={() => onViewSelect("user")}>Calendar</button>
            <button className="nav-tab" onClick={() => onViewSelect("my-events")}>My Events</button>
            <button className="nav-tab" onClick={() => onViewSelect("connections")}>My Connections</button>
          </div>

          <div className="nav-right">
            <p style={{ margin: 0, fontSize: 12, opacity: 0.6, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {currentUser?.user?.id}
            </p>

            <button
              type="button"
              className="nav-icon-circle nav-icon-button nav-notification-button"
              aria-label="Notifications"
              onClick={() => onViewSelect("notifications")}
            >
              <svg
                className="nav-bell-icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 8a6 6 0 10-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
                <path d="M13.73 21a2 2 0 01-3.46 0" />
              </svg>
            </button>

            <div className="nav-avatar-circle">
              <button
                className="nav-tab nav-profile-button active"
                onClick={() => onViewSelect("user-profile")}
                aria-label="Profile"
              >
                <svg
                  className="nav-user-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* HERO / COVER HEADER */}
        <div className="profileHero">
          <div className="profileCover" style={bannerStyle}>
            <div className="profileCoverOverlay" />
          </div>

          <div className="profileHeroInner">
            <div className="profileAvatarWrapper">
              <div className="profileAvatarLarge" aria-label="Profile avatar">
                {meta?.avatar_url
                  ? <img src={meta.avatar_url} alt="avatar" style={{ width: "100%", height: "100%", borderRadius: "22px", objectFit: "cover" }} />
                  : initials
                }
              </div>
            </div>

            <div className="profileHeroText">
              <div className="profileHeroName">{profile.fullName || "Your Name"}</div>
              <div className="profileHeroHandle">@{profile.username || "username"}</div>

              <div className="profileHeroBadges">
                <span className="badge">Age: {ageDisplay}</span>
                <span className="badge">Gender: {genderDisplay}</span>
                <span className={"badge " + (meta?.has_setup_profile ? "ok" : "warn")}>
                  {meta?.has_setup_profile ? "Profile Complete" : "Setup Required"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* STATS — now wired to real counts */}
        <div className="profileStats">
          <div className="statCard">
            <div className="statLabel">Preferences</div>
            <div className="statValue">{prefsDisplay.length}</div>
          </div>

          <div className="statCard">
            <div className="statLabel">Connections</div>
            <div className="statValue">
              {eventsLoading ? "…" : (connectionsCount ?? 0)}
            </div>
          </div>

          <div className="statCard">
            <div className="statLabel">Going</div>
            <div className="statValue">
              {eventsLoading ? "…" : goingEvents.length}
            </div>
          </div>
        </div>

        {/* CONTENT GRID */}
        <div className="profileGridNew">
          {/* About */}
          <div className="profileCard">
            <div className="profileCardTitle">About</div>
            <div className="profileCardBody">
              <p className="aboutText">{bioDisplay}</p>
            </div>
          </div>

          {/* Preferences */}
          <div className="profileCard">
            <div className="profileCardTitle">Preferences</div>
            <div className="profileCardBody">
              {prefsDisplay.length === 0 ? (
                <div className="muted">No preferences yet.</div>
              ) : (
                <div className="prefsChips">
                  {prefsDisplay.map((p) => (
                    <span key={p} className="prefPill">{p}</span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Basics */}
          <div className="profileCard">
            <div className="profileCardTitle">Basics</div>
            <div className="profileCardBody">
              <div className="kvRow">
                <div className="kvKey">Name</div>
                <div className="kvValue">{meta?.name || profile.fullName || "—"}</div>
              </div>
              <div className="kvRow">
                <div className="kvKey">Email</div>
                <div className="kvValue">{profile.email || "—"}</div>
              </div>
              <div className="kvRow">
                <div className="kvKey">Age</div>
                <div className="kvValue">{ageDisplay}</div>
              </div>
              <div className="kvRow">
                <div className="kvKey">Gender</div>
                <div className="kvValue">{genderDisplay}</div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="profileCard">
            <div className="profileCardTitle">Quick Actions</div>
            <div className="profileCardBody">
              <button
                className="actionBtn"
                onClick={() => { setSetupStep(1); setShowSetupModal(true); }}
              >
                Edit Profile
              </button>

              <button className="actionBtn secondary" onClick={() => onViewSelect("my-events")}>
                Go to My Events
              </button>

              <button className="actionBtn secondary" onClick={() => onViewSelect("connections")}>
                See My Connections
              </button>

              <button className="actionBtn secondary" onClick={() => {
                sessionStorage.removeItem("currentUser");
                sessionStorage.removeItem("currentView");
                onViewSelect("loginSignup");
              }}>
                Log Out
              </button>
            </div>
          </div>

          {/* Going Events */}
          <div className="profileCard" style={{ gridColumn: "1 / -1" }}>
            <div className="profileCardTitle">Event History</div>
            <div className="profileCardBody">
              {eventsLoading ? (
                <div className="muted">Loading…</div>
              ) : goingEvents.length === 0 ? (
                <div className="muted">No upcoming events yet.</div>
              ) : (
                <div className="profileEventGrid">
                  {goingEvents.slice(0, 6).map((event) => (
                    <EventRow key={event.id} event={event} />
                  ))}
                  {goingEvents.length > 6 && (
                    <div className="profileEventMore">+{goingEvents.length - 6} more</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}